import { useState, useEffect } from "react";
import { useAnimate, stagger, motion, AnimatePresence } from "framer-motion";
import { Fade as Hamburger } from "hamburger-react";
import { Link, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../../config/firebaseConfig";
import { Building2, type LucideIcon, Newspaper } from "lucide-react";
import HomeSvg from "../../../assets//nav/home.svg?react";
import AtuacaoSvg from "../../../assets/nav/occupation.svg?react";
import ContatoSvg from "../../../assets//nav/contact.svg?react";
// import ClienteSvg from "../../../../public/nav/customers.svg?react";
import { FaRegUserCircle } from "react-icons/fa";

type AnimationStep = [
  string,
  { [key: string]: string | number },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { [key: string]: any },
];

type MenuItem = {
  path: string;
  label: string;
  icon?: LucideIcon | React.ElementType;
  svg?: React.ElementType;
};

const useMenuAnimation = (isOpen: boolean) => {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const menuAnimations: AnimationStep[] = isOpen
      ? [
          // [
          //   "nav",
          //   { transform: "translateX(0%)" },
          //   {
          //     ease: [0.08, 0.65, 0.53, 0.96],
          //     duration: 0.45,
          //   },
          // ],
          [
            "li",
            {
              transform: "translateX(0px)",
              opacity: 1,
              filter: "blur(0px)",
            },
            {
              delay: stagger(0.05),
              at: "-0.2",
            },
          ],
        ]
      : [
          [
            "li",
            {
              transform: "translateX(20px)",
              opacity: 0,
              filter: "blur(8px)",
            },
            {
              delay: stagger(0.03, { from: "last" }),
              at: "<",
            },
          ],
          // [
          //   "nav",
          //   { transform: "translateX(100%)" },
          //   {
          //     ease: [0.08, 0.65, 0.53, 0.96],
          //     duration: 0.45,
          //     at: "-0.1",
          //   },
          // ],
        ];

    animate([
      [
        "path.top",
        {
          d: isOpen ? "M 3 16.5 L 17 2.5" : "M 2 2.5 L 20 2.5",
        },
        { at: "<" },
      ],

      ["path.middle", { opacity: isOpen ? 0 : 1 }, { at: "<" }],

      [
        "path.bottom",
        {
          d: isOpen ? "M 3 2.5 L 17 16.346" : "M 2 16.346 L 20 16.346",
        },
        { at: "<" },
      ],

      ...menuAnimations,
    ]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return scope;
};

interface MenuMobileProps {
  toggle: () => void;
}

export const MenuMobile = ({ toggle }: MenuMobileProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const scope = useMenuAnimation(isOpen);

  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
    });

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      unsubscribe();
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  useEffect(() => {
    setIsOpen(false);
    toggle();

    window.scrollTo(0, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const closeMenu = () => {
    setIsOpen(false);
    toggle();
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* HEADER */}
      {/* shadow-[0_8px_40px_rgba(0,0,0,0.35)] */}
      <div
        ref={scope}
        className={`
  fixed
  left-1/2
  top-2
  z-[80]
  flex
  h-20
  w-[98%]
  -translate-x-1/2
  transform-gpu
  items-center
  justify-between
  rounded-[22px]
  border
  px-4
  shadow-sm
  backdrop-blur-sm
  transition-all
  duration-500
  lg:hidden

  ${
    isScrolled
      ? `
        border-[#00CAD8]/30
        bg-gradient-to-br
        from-[#00363D]/90
        via-[#00282D]/90
        to-[#000C0F]/90
      `
      : `
        border-[#00CAD8]/20
        bg-gradient-to-br
        from-[#00363D]/80
        via-[#00282D]/80
        to-[#000C0F]/80
      `
  }
`}
      >
        <Link to="/" aria-label="Home" onClick={closeMenu}>
          <img src="/logo/logoblueII.svg" alt="Miranda Logo" className="ml-1 h-14" />
        </Link>
      </div>

      {/* BOTÃO HAMBURGER */}
      <button
        title="menu-hamburger"
        aria-label={isOpen ? "Close menu" : "Open menu"}
        className="
      fixed
      right-6
      top-6
      z-[130]
      flex
      h-12
      w-12
      items-center
      justify-center
      rounded-full
      border-[0.1px]
      border-white/10
      bg-[#032f35]/90
      shadow-[0_0_25px_rgba(0,202,216,0.12)]
      lg:hidden
    "
        // backdrop-blur-xl

        onClick={() => setIsOpen(!isOpen)}
      >
        <Hamburger
          direction="right"
          color={isOpen ? "#00CAD8" : "white"}
          size={24}
          rounded
          duration={0.4}
          distance="sm"
          toggled={isOpen}
          toggle={setIsOpen}
        />
      </button>

      {/* OVERLAY */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeMenu}
            className="
          fixed
          inset-0
          z-[90]
          bg-[#00363D]/70

          lg:hidden
        "
          />
        )}
      </AnimatePresence>

      {/* MENU */}
      <AnimatePresence>
        {isOpen && (
          <motion.nav
            initial={{
              x: "100%",
              opacity: 0,
              scale: 0.98,
            }}
            animate={{
              x: 0,
              opacity: 1,
              scale: 1,
            }}
            exit={{
              x: "100%",
              opacity: 0,
              scale: 0.98,
            }}
            transition={{
              type: "spring",
              damping: 24,
              stiffness: 260,
            }}
            className="
          fixed
          right-0
          top-2
          z-[100]
          h-[660px]
          w-full
          transform-gpu
          overflow-hidden
          rounded-[22px]
          border-[0.1px]
          border-[#00CAD8]/20
          bg-gradient-to-br
          from-[#00363D]/70
          via-[#00282D]/70
          to-[#000C0F]/70
          px-6
          pt-12
          shadow-xl
          backdrop-blur-lg

          will-change-transform
          lg:hidden
        "
            // shadow-[-10px_0_50px_rgba(0,0,0,0.55)]
          >
            {/* LOGO MENU */}
            <motion.div
              initial={{
                opacity: 0,
                x: -10,
              }}
              animate={{
                opacity: 4,
                x: 0,
              }}
              transition={{
                delay: 0.1,
                duration: 0.4,
              }}
              className="
            mb-9
            flex
            items-center
            justify-start
          "
            >
              <img src="/logo/logoblueII.svg" alt="Miranda Logo" className="-mt-7 h-14" />
            </motion.div>

            {/* LINKS */}
            <motion.ul
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: 0.08,
                  },
                },
              }}
              className="flex flex-col gap-3"
            >
              {[
                {
                  path: "/",
                  label: "Início",
                  svg: HomeSvg,
                },

                {
                  path: "/escritorio",
                  label: "Escritório",
                  icon: Building2,
                },

                {
                  path: "/area-atuacao",
                  label: "Área de atuação",
                  svg: AtuacaoSvg,
                },

                {
                  path: "/noticias",
                  label: "Notícias",
                  icon: Newspaper,
                },

                {
                  path: "/contato",
                  label: "Contato",
                  svg: ContatoSvg,
                },

                {
                  path: isAuthenticated ? "/dashboard" : "/login",

                  label: "Área do cliente",
                  icon: FaRegUserCircle,
                },
              ].map((item: MenuItem) => {
                const Icon = item.icon;
                const Svg = item.svg;

                return (
                  <motion.li
                    key={item.label}
                    variants={{
                      hidden: {
                        opacity: 0,
                        x: 40,
                        filter: "blur(8px)",
                      },
                      // hidden: {
                      //   opacity: 0,
                      //   x: 20,
                      // },

                      visible: {
                        opacity: 1,
                        x: 0,
                        filter: "blur(0px)",
                      },
                    }}
                    transition={{
                      duration: 0.45,
                    }}
                  >
                    <Link
                      to={item.path}
                      onClick={closeMenu}
                      className={`
                    group
                    flex
                    items-center
                    gap-4
                    rounded-2xl
                    border
                    px-4
                    py-3
                    text-lg
                    font-normal
                    transition-all
                    duration-300

                    ${
                      isActive(item.path)
                        ? `
                          border-[#00CAD8]/30
                          bg-[#00CAD8]/10
                          font-semibold
                          text-[#00CAD8]
                          shadow-[0_0_25px_rgba(0,202,216,0.08)]
                        `
                        : `
                          border-white/5
                          bg-white/[0.03]
                          font-normal
                          text-white
                          hover:border-white/10
                          hover:bg-white/[0.06]
                        `
                    }
                  `}
                    >
                      <div
                        className="
    flex
    h-11
    w-11
    items-center
    justify-center
    rounded-xl
    bg-[#00CAD8]/10
    transition-all
    duration-300
    group-hover:scale-110
    group-hover:bg-[#00CAD8]/15
  "
                      >
                        {Icon && (
                          <Icon
                            className={`
      h-6
      w-6
      transition-colors

      ${isActive(item.path) ? "text-[#00CAD8]" : "text-white"}
    `}
                          />
                        )}

                        {Svg && (
                          <Svg
                            className={`
      h-6
      w-6
      transition-all

      ${isActive(item.path) ? "fill-[#00CAD8] text-[#00CAD8]" : "fill-white text-white"}
    `}
                          />
                        )}
                      </div>

                      {item.label}
                    </Link>
                  </motion.li>
                );
              })}
            </motion.ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
};

export default MenuMobile;
